import { JobListItem } from './jobListItem'
import { Button, Empty, Space, Table, TableColumnType, TableProps } from 'antd'
import { useJobs, useRestoreJobMutation } from './jobListApi'
import { EstimateStatus, EstimateStatuses, estimateStatusName, ProjectStatus, ProjectStatuses, projectStatusName, ProjectType, ProjectTypes } from '../../common/constants'
import { centsToDollarsDisplay } from '../displayFormatters'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck, faCalendarXmark, faCalendarCheck, faWarning, faTrashRestore, faPaperPlane } from "@fortawesome/pro-solid-svg-icons"
import { faPaperPlane as faPaperPlaneDuoTone, faThumbsUp, faXmarkToSlot } from "@fortawesome/pro-duotone-svg-icons"
import VSpace from '../common/VSpace'
import { blue, green, grey, red } from '@ant-design/colors'
import { SorterResult } from 'antd/es/table/interface'
import { JobListFilterState } from './useJobListFilters'

export type Props = {
  jobType?: ProjectType
  sold?: boolean
  deleted?: boolean
  filters: JobListFilterState
}

type KeysOfType<T, TProperty> = { [k in keyof T]: T[k] extends TProperty ? k : never }[keyof T]

const stringSorter = (property: KeysOfType<JobListItem, string>) => (a: JobListItem, b: JobListItem): number => {
  return (a[property] ?? '').localeCompare(b[property] ?? '')
}

const booleanSorter = (property: KeysOfType<JobListItem, boolean>) => (a: JobListItem, b: JobListItem): number => {
  const aValue = a[property] ?? false
  const bValue = b[property] ?? false

  if (!aValue && bValue) {
    return -1
  }

  if (aValue && !bValue) {
    return 1
  }

  return 0
}

const JobList = ({ jobType, deleted = false, sold = true, filters }: Props) => {
  const {
    sortColumn, setSortColumn,
    sortOrder, setSortOrder,
    page, setPage,
    pageSize, setPageSize,
    anyFiltersSet, clearFilters
  } = filters

  const jobs = useJobs({ ...filters.request, page, pageSize }, deleted)

  const restoreJobMutation = useRestoreJobMutation((item: JobListItem) => {
    window.location.href = `#!/${item.url}`
  })

  const statusIcons = {
    [ProjectStatuses.Unscheduled]: <FontAwesomeIcon icon={faCalendarXmark} color={grey.primary} size="lg" />,
    [ProjectStatuses.Scheduled]: <FontAwesomeIcon icon={faCalendarCheck} color={blue.primary} size="lg" />,
    [ProjectStatuses.WaitingOnParts]: <FontAwesomeIcon icon={faWarning} color={red.primary} size="lg" />,
    [ProjectStatuses.Completed]: <FontAwesomeIcon icon={faCheck} color={green.primary} size="lg" />,
  }

  const estimateStatusIcons = {
    [EstimateStatuses.Unsent]: <FontAwesomeIcon icon={faPaperPlane} color={grey[0]} size="lg" />,
    [EstimateStatuses.Sent]: <FontAwesomeIcon icon={faPaperPlaneDuoTone} color={blue.primary} size="lg" />,
    [EstimateStatuses.Approved]: <FontAwesomeIcon icon={faThumbsUp} color={green.primary} size="lg" />,
    [EstimateStatuses.Lost]: <FontAwesomeIcon icon={faXmarkToSlot} color={red.primary} size="lg" />
  }

  const stringColumn = (property: KeysOfType<JobListItem, string>, title: string): TableColumnType<JobListItem> => {
    return {
      title,
      dataIndex: property,
      sorter: stringSorter(property),
      sortOrder: sortColumn === property ? sortOrder : undefined
    }
  }

  const booleanColumn = (property: KeysOfType<JobListItem, boolean>, title: string, jobTypes: ProjectType[]): TableColumnType<JobListItem>[] => {
    if (!jobType || !jobTypes.includes(jobType)) {
      return []
    }
    return [{
      title,
      dataIndex: property,
      sorter: booleanSorter(property),
      sortOrder: sortColumn === property ? sortOrder : undefined,
      align: 'center',
      render: (value) => {
        if (value) {
          return <FontAwesomeIcon icon={faCheck} />
        }
        return <></>
      }
    }]
  }

  const soldColumn: TableColumnType<JobListItem>[] = !sold ? [] : [{
    title: 'Sold',
    dataIndex: 'soldDate',
    render: (soldDate) => {
      return soldDate && new Date(soldDate).toLocaleDateString()
    },
    sorter: stringSorter('soldDate'),
    sortOrder: sortColumn === 'soldDate' ? sortOrder : undefined
  }]

  const estimateDateColumn: TableColumnType<JobListItem>[] = sold ? [] : [{
    title: 'Estimated',
    dataIndex: 'estimateDateUtc',
    render: (estimateDateUtc) => {
      return estimateDateUtc && new Date(estimateDateUtc).toLocaleDateString()
    },
    sorter: stringSorter('estimateDateUtc'),
    sortOrder: sortColumn === 'estimateDateUtc' ? sortOrder : undefined
  }]

  const finalPriceColumn: TableColumnType<JobListItem>[] = deleted ? [] : [{
    title: 'Final Price',
    dataIndex: 'finalPrice',
    render: centsToDollarsDisplay,
    sorter: (a, b) => a.finalPrice - b.finalPrice,
    sortOrder: sortColumn === 'finalPrice' ? sortOrder : undefined
  }]

  const statusColumn: TableColumnType<JobListItem>[] = !sold || deleted ? [] : [{
    title: 'Status',
    dataIndex: 'status',
    render: (status: ProjectStatus) => {
      return <Space>
        {statusIcons[status]}
        {projectStatusName(status)}
      </Space>
    },
    sorter: (a, b) => a.status - b.status,
    sortOrder: sortColumn === 'status' ? sortOrder : undefined
  }]

  const estimateStatusColumn: TableColumnType<JobListItem>[] = sold || deleted ? [] : [{
    title: 'Status',
    dataIndex: 'estimateStatus',
    render: (estimateStatus: EstimateStatus, record: JobListItem) => {
      return <VSpace>
        <Space>
          {estimateStatusIcons[estimateStatus]}
          {estimateStatusName(estimateStatus)}
        </Space>
        {record.estimateStatusDateUtc && new Date(record.estimateStatusDateUtc).toLocaleDateString()}
      </VSpace>
    },
    sorter: (a, b) => a.status - b.status,
    sortOrder: sortColumn === 'estimateStatus' ? sortOrder : undefined
  }]

  const restoreColumn: TableColumnType<JobListItem>[] = !deleted ? [] : [{
    title: 'Restore',
    align: 'center',
    render: (_: any, item: JobListItem) => {
      return <Button
        ghost
        size="large"
        icon={<FontAwesomeIcon icon={faTrashRestore}
          color={green.primary} />}
        title="Restore"
        loading={restoreJobMutation.isLoading}
        onClick={() => restoreJobMutation.mutate(item)}
      />
    }
  }]

  const columns: TableColumnType<JobListItem>[] = [
    stringColumn('jobNumber', 'No.'),
    ...soldColumn,
    ...estimateDateColumn,
    stringColumn('name', 'Name'),
    stringColumn('customer', 'Customer'),
    stringColumn('property', 'Property'),
    ...finalPriceColumn,
    stringColumn('salesPerson', 'Sales Person'),
    ...booleanColumn('active', 'Active', [ProjectTypes.Ticket, ProjectTypes.Maintenance]),
    ...statusColumn,
    ...estimateStatusColumn,
    ...restoreColumn,
  ]

  const onTableChange: TableProps<JobListItem>['onChange'] = (_pagination, _filters, sorter) => {
    const sorterResult = (sorter as SorterResult<JobListItem>)
    setSortColumn(sorterResult.field as string | undefined)
    setSortOrder(sorterResult.order)
  }

  return <VSpace size="large">
    <Table
      columns={columns}
      dataSource={jobs.data?.jobs ?? []}
      rowKey="id"
      showSorterTooltip={false}
      onChange={onTableChange}
      pagination={{
        pageSize,
        current: page,
        total: jobs.data?.totalRows ?? 0,
        onChange: (page, pageSize) => {
          setPage(page)
          setPageSize(pageSize)
        }
      }}
      onRow={(item) => {
        if (deleted) {
          return {}
        }

        return {
          onClick: () => {
            window.location.href = `#!/${item.url}`
          },
          style: {
            cursor: 'pointer'
          }
        }
      }}
      locale={{
        emptyText: <Empty description={<Space direction="vertical">
          <span>No Results</span>
          {anyFiltersSet && <Button type="primary" onClick={clearFilters}>Clear Filters</Button>}
        </Space>} />
      }}
      loading={jobs.isLoading}
    />
  </VSpace >
}

export default JobList
